import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Popover } from 'radix-ui';
import { ImagePlus } from 'lucide-react';
import { useStore } from '../db/context';
import { uploadAttachment } from '../db/attachments';
import { useCatalog } from '../state/catalog';
import { CloseButton } from './CloseButton';
import { PageIcon } from './PageIcon';
import { IMAGE_ICON, isImageIcon } from '../lib/pageIcon';

/** A short list that covers most pages; any other emoji can be typed or pasted. */
// prettier-ignore
const ICONS = [
  '✅', '☑️', '📋', '📌', '📥', '📤', '📅', '🗓️', '⏰', '⭐',
  '🚀', '🎯', '💡', '🧭', '📚', '📖', '📝', '✏️', '🗂️', '📁',
  '🏠', '👤', '👥', '🤝', '💬', '📞', '☎️', '📧', '💼', '🏢',
  '💻', '🖥️', '🔧', '⚙️', '🛠️', '🔒', '🔑', '🌐', '📡', '🔌',
  '📊', '📈', '💰', '🧾', '🧩', '🔥', '❤️', '🌱', '🎉', '🗄️',
  '🟥', '🟧', '🟨', '🟩', '🟦', '🟪', '⬛', '⬜', '🔴', '🟢',
  '📣', '🔔', '🧪', '🧠', '🎓', '🏆', '🛒', '🚗', '✈️', '🏖️',
];

/** First grapheme of what was typed: one emoji, even with modifiers. */
function firstGrapheme(text: string): string {
  const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  for (const { segment } of seg.segment(text.trim())) return segment;
  return '';
}

/**
 * Choose or remove the icon of a page (shown in the sidebar, tabs and breadcrumb):
 * an emoji, or an image of your own (uploaded once, then offered for other pages too).
 */
export function IconPicker({ pageId, children }: { pageId: string; children: ReactNode }) {
  const { t } = useTranslation();
  const store = useStore();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState('');
  const [error, setError] = useState(false);
  const file = useRef<HTMLInputElement>(null);
  const pages = useCatalog((s) => s.pages);
  const own = [...new Set(pages.map((p) => p.icon).filter(isImageIcon))] as string[];
  const choose = (icon: string | null) => {
    void store.updatePage(pageId, { icon });
    setCustom('');
    setOpen(false);
  };
  const upload = async (f: File | undefined) => {
    if (!f) return;
    setError(false);
    // Icons are small: anything up to 2 MB.
    const name = f.size <= 2 * 1024 * 1024 ? await uploadAttachment(store, f) : null;
    if (name) choose(`${IMAGE_ICON}${name}`);
    else setError(true);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          aria-label={t('page.icon.title')}
          className="nx-pop-in z-[70] flex w-80 flex-col gap-2 rounded-xl border border-border bg-bg p-2 shadow-pop"
        >
          <div className="-mb-1 flex items-center justify-between pl-1">
            <span className="text-xs font-medium text-fg-muted">{t('page.icon.title')}</span>
            <Popover.Close asChild>
              <CloseButton className="size-6" />
            </Popover.Close>
          </div>
          <div className="grid max-h-56 grid-cols-10 gap-0.5 overflow-y-auto">
            {ICONS.map((icon) => (
              <button
                key={icon}
                type="button"
                aria-label={icon}
                onClick={() => choose(icon)}
                className="grid size-7 place-items-center rounded-md text-base hover:bg-bg-hover"
              >
                {icon}
              </button>
            ))}
          </div>
          {own.length > 0 && (
            <div>
              <p className="mb-1 pl-1 text-xs text-fg-muted">{t('page.icon.own')}</p>
              <div className="grid grid-cols-10 gap-0.5">
                {own.map((icon) => (
                  <button
                    key={icon}
                    type="button"
                    aria-label={icon.slice(IMAGE_ICON.length)}
                    onClick={() => choose(icon)}
                    className="grid size-7 place-items-center rounded-md text-lg hover:bg-bg-hover"
                  >
                    <PageIcon icon={icon} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <form
            className="flex gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              const icon = firstGrapheme(custom);
              if (icon) choose(icon);
            }}
          >
            <input
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder={t('page.icon.custom')}
              aria-label={t('page.icon.custom')}
              className="min-w-0 flex-1 rounded-lg border border-border bg-bg px-2 py-1 text-sm"
            />
            <button
              type="submit"
              className="rounded-full px-2 py-1 text-sm text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              {t('page.icon.use')}
            </button>
          </form>
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => file.current?.click()}
              className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs hover:bg-bg-hover"
            >
              <ImagePlus className="size-3.5" />
              {t('page.icon.upload')}
            </button>
            <input
              ref={file}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
              className="hidden"
              onChange={(e) => {
                void upload(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => choose(null)}
              className="rounded px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-danger"
            >
              {t('page.icon.remove')}
            </button>
          </div>
          {error && (
            <p role="alert" className="text-xs text-danger">
              {t('page.icon.tooLarge')}
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
